/**
 * Shamir Secret Sharing implementation
 * Uses GF(256) for operations and base32 encoding for compact, typeable shares
 */

import { stringToUint8Array, uint8ArrayToString, generateRandomBytes } from './utils';

// Base32 alphabet (RFC 4648) - easier to type and less ambiguous than base64
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * Encode bytes to base32 string (compact and human-friendly)
 */
function encodeBase32(data: Uint8Array): string {
  let result = '';
  let buffer = 0;
  let bitsLeft = 0;

  for (const byte of data) {
    buffer = (buffer << 8) | byte;
    bitsLeft += 8;

    while (bitsLeft >= 5) {
      result += BASE32_ALPHABET[(buffer >> (bitsLeft - 5)) & 31];
      bitsLeft -= 5;
    }
  }

  if (bitsLeft > 0) {
    result += BASE32_ALPHABET[(buffer << (5 - bitsLeft)) & 31];
  }

  return result;
}

/**
 * Decode base32 string to bytes
 */
function decodeBase32(str: string): Uint8Array {
  const normalized = str.toUpperCase().replace(/[^A-Z2-7]/g, '');
  const result: number[] = [];
  let buffer = 0;
  let bitsLeft = 0;

  for (const char of normalized) {
    const value = BASE32_ALPHABET.indexOf(char);
    if (value === -1) {
      throw new Error(`Invalid base32 character: ${char}`);
    }

    buffer = (buffer << 5) | value;
    bitsLeft += 5;

    if (bitsLeft >= 8) {
      result.push((buffer >> (bitsLeft - 8)) & 255);
      bitsLeft -= 8;
    }
  }

  return new Uint8Array(result);
}

/**
 * GF(256) multiplication using log/antilog tables
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class GF256 {
  private static readonly LOG_TABLE: number[] = [];
  private static readonly EXP_TABLE: number[] = [];
  private static initialized = false;

  private static initialize(): void {
    if (this.initialized) return;

    let x = 1;
    for (let i = 0; i < 255; i++) {
      this.EXP_TABLE[i] = x;
      this.LOG_TABLE[x] = i;
      x = (x << 1) ^ (x & 128 ? 0x11d : 0);
    }
    const firstExp = this.EXP_TABLE[0];
    if (firstExp !== undefined) {
      this.EXP_TABLE[255] = firstExp;
    }

    this.initialized = true;
  }

  static multiply(a: number, b: number): number {
    this.initialize();

    if (a === 0 || b === 0) {
      return 0;
    }

    const logA = this.LOG_TABLE[a];
    const logB = this.LOG_TABLE[b];
    if (logA === undefined || logB === undefined) {
      throw new Error('Invalid GF(256) value');
    }

    const result = this.EXP_TABLE[(logA + logB) % 255];
    if (result === undefined) {
      throw new Error('Invalid GF(256) value');
    }

    return result;
  }

  static divide(a: number, b: number): number {
    this.initialize();

    if (b === 0) {
      throw new Error('Division by zero');
    }
    if (a === 0) {
      return 0;
    }

    const logA = this.LOG_TABLE[a];
    const logB = this.LOG_TABLE[b];
    if (logA === undefined || logB === undefined) {
      throw new Error('Invalid GF(256) value');
    }

    const result = this.EXP_TABLE[(logA - logB + 255) % 255];
    if (result === undefined) {
      throw new Error('Invalid GF(256) value');
    }

    return result;
  }

  static add(a: number, b: number): number {
    return a ^ b;
  }
}

/**
 * Evaluate polynomial at point x in GF(256)
 */
function evaluatePolynomial(coefficients: Uint8Array, x: number): number {
  let result = 0;

  for (let i = coefficients.length - 1; i >= 0; i--) {
    const coeff = coefficients[i];
    if (coeff === undefined) {
      throw new Error('Invalid coefficient');
    }
    result = GF256.add(GF256.multiply(result, x), coeff);
  }

  return result;
}

/**
 * Lagrange interpolation at x=0 to reconstruct secret in GF(256)
 */
function interpolate(shares: { x: number; y: number }[]): number {
  let result = 0;

  for (let i = 0; i < shares.length; i++) {
    const shareI = shares[i];
    if (!shareI) {
      throw new Error('Invalid share data');
    }

    let numerator = 1;
    let denominator = 1;

    for (let j = 0; j < shares.length; j++) {
      if (i === j) continue;

      const shareJ = shares[j];
      if (!shareJ) {
        throw new Error('Invalid share data');
      }

      numerator = GF256.multiply(numerator, shareJ.x);
      denominator = GF256.multiply(denominator, GF256.add(shareI.x, shareJ.x));
    }

    const term = GF256.multiply(shareI.y, GF256.divide(numerator, denominator));
    result = GF256.add(result, term);
  }

  return result;
}

export interface ShamirShare {
  index: number;
  share: string; // Base32 encoded
}

/**
 * Compute SHA-256 hash of data and return first 8 bytes as checksum
 */
async function computeChecksum(data: Uint8Array): Promise<Uint8Array> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data as BufferSource);
  const hashArray = new Uint8Array(hashBuffer);
  return hashArray.slice(0, 8); // Use first 8 bytes (64 bits) as checksum
}

/**
 * Split a secret into n shares with threshold k
 * @param secret - The secret to split (string)
 * @param totalShares - Total number of shares to generate (n)
 * @param threshold - Minimum shares needed to reconstruct (k)
 * @returns Array of shares in compact format
 */
export async function splitSecret(
  secret: string,
  totalShares: number,
  threshold: number
): Promise<ShamirShare[]> {
  if (threshold < 2) {
    throw new Error('Threshold must be at least 2');
  }
  if (totalShares < threshold) {
    throw new Error('Total shares must be at least equal to threshold');
  }
  if (totalShares > 255) {
    throw new Error('Maximum 255 shares supported');
  }
  if (!secret || secret.length === 0) {
    throw new Error('Secret cannot be empty');
  }

  const secretBytes = stringToUint8Array(secret);

  // Compute checksum and prepend to secret
  const checksum = await computeChecksum(secretBytes);
  const secretWithChecksum = new Uint8Array(checksum.length + secretBytes.length);
  secretWithChecksum.set(checksum, 0);
  secretWithChecksum.set(secretBytes, checksum.length);

  const shares: ShamirShare[] = [];

  // For each byte of the secret (with checksum), create a polynomial and evaluate at share points
  const shareData: number[][] = Array.from({ length: totalShares }, () => []);

  for (const secretByte of secretWithChecksum) {

    // Create polynomial: f(x) = secret_byte + coeff1*x + coeff2*x^2 + ...
    const coefficients = new Uint8Array(threshold);
    coefficients[0] = secretByte; // Secret is constant term

    // Generate random coefficients for higher degree terms
    const randomCoeffs = generateRandomBytes(threshold - 1);
    coefficients.set(randomCoeffs, 1);

    // Evaluate polynomial at points 1, 2, 3, ..., n
    for (let shareIndex = 0; shareIndex < totalShares; shareIndex++) {
      const x = shareIndex + 1; // x values are 1-indexed (never use 0)
      const y = evaluatePolynomial(coefficients, x);
      const shareArray = shareData[shareIndex];
      if (!shareArray) {
        throw new Error('Invalid share data');
      }
      shareArray.push(y);
    }
  }

  // Convert share data to base32 strings
  for (let i = 0; i < totalShares; i++) {
    const data = shareData[i];
    if (!data) {
      throw new Error('Invalid share data');
    }
    const shareBytes = new Uint8Array(data);
    const encoded = encodeBase32(shareBytes);
    shares.push({
      index: i + 1,
      share: `${i + 1}-${encoded}`,
    });
  }

  return shares;
}

/**
 * Reconstruct a secret from shares
 * @param shareStrings - Array of share strings (e.g., ["1-ABC...", "3-XYZ..."])
 * @returns The reconstructed secret
 */
export async function reconstructSecret(shareStrings: string[]): Promise<string> {
  if (shareStrings.length < 2) {
    throw new Error('At least 2 shares are required');
  }

  // Parse shares
  const parsedShares: { index: number; data: Uint8Array }[] = [];

  for (const shareStr of shareStrings) {
    const trimmed = shareStr.trim();
    if (!trimmed) continue;

    const parts = trimmed.split('-');
    if (parts.length < 2) {
      throw new Error(`Invalid share format: ${shareStr}. Expected format: "1-ABC..."`);
    }

    const indexStr = parts[0];
    if (!indexStr) {
      throw new Error(`Invalid share format: ${shareStr}`);
    }

    const index = parseInt(indexStr, 10);
    if (isNaN(index) || index < 1 || index > 255) {
      throw new Error(`Invalid share index: ${indexStr}`);
    }

    const encoded = parts.slice(1).join('-'); // In case data contains dashes
    const data = decodeBase32(encoded);

    parsedShares.push({ index, data });
  }

  if (parsedShares.length === 0) {
    throw new Error('No valid shares provided');
  }

  // Verify all shares have the same length
  const firstShare = parsedShares[0];
  if (!firstShare) {
    throw new Error('Invalid share data');
  }

  const shareLength = firstShare.data.length;
  for (const share of parsedShares) {
    if (share.data.length !== shareLength) {
      throw new Error('All shares must have the same length');
    }
  }

  // Reconstruct each byte of the secret (with checksum)
  const reconstructedBytes = new Uint8Array(shareLength);

  for (let byteIndex = 0; byteIndex < shareLength; byteIndex++) {
    const points = parsedShares.map((share) => {
      const byte = share.data[byteIndex];
      if (byte === undefined) {
        throw new Error('Invalid share data');
      }
      return {
        x: share.index,
        y: byte,
      };
    });

    reconstructedBytes[byteIndex] = interpolate(points);
  }

  // Verify checksum (first 8 bytes)
  const CHECKSUM_LENGTH = 8;
  if (reconstructedBytes.length < CHECKSUM_LENGTH) {
    throw new Error('Invalid share data: too short');
  }

  const storedChecksum = reconstructedBytes.slice(0, CHECKSUM_LENGTH);
  const secretBytes = reconstructedBytes.slice(CHECKSUM_LENGTH);

  // Compute expected checksum
  const expectedChecksum = await computeChecksum(secretBytes);

  // Compare checksums
  let checksumMatch = true;
  for (let i = 0; i < CHECKSUM_LENGTH; i++) {
    if (storedChecksum[i] !== expectedChecksum[i]) {
      checksumMatch = false;
      break;
    }
  }

  if (!checksumMatch) {
    throw new Error(
      'Checksum verification failed. The shares may be corrupted, incorrect, or insufficient. Please verify:\n' +
        '• Share indices are correct (1, 2, 3, etc.)\n' +
        '• Shares are from the same secret\n' +
        '• You have enough shares to meet the threshold'
    );
  }

  return uint8ArrayToString(secretBytes);
}

/**
 * Format a share for display (adds visual separators every 4 characters)
 */
export function formatShareForDisplay(share: string): string {
  const parts = share.split('-');
  if (parts.length < 2) return share;

  const index = parts[0];
  const data = parts.slice(1).join('-');

  // Add spaces every 4 characters for readability
  const formatted = data.match(/.{1,4}/g)?.join(' ') || data;

  return `${index}-${formatted}`;
}

/**
 * Clean a share string (remove spaces, normalize)
 */
export function cleanShareString(share: string): string {
  return share.replace(/\s+/g, '').toUpperCase();
}
